/**
 * Compact, collapsible side panel for managing the standard items catalog
 * inline with the Per-Room Defaults tab. Keeps the tab focused on its
 * primary job (choosing what goes into each room) while letting admins
 * curate the master list without leaving the page.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Trash2, Pencil, Search, ChevronDown, ChevronRight, X,
  Lock, Box,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getFurnitureItemsCatalog,
  upsertFurnitureCatalogItem,
  deleteFurnitureCatalogItem,
  renameFurnitureCatalogItem,
  getFurnitureItemCategories,
  createFurnitureItemCategory,
  renameFurnitureItemCategory,
  deleteFurnitureItemCategory,
} from '@/lib/api';

export function ItemsCatalogPanel({ readOnly }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [renamingCat, setRenamingCat] = useState(null);
  const [catNameDraft, setCatNameDraft] = useState('');
  const [renamingItemKey, setRenamingItemKey] = useState(null);
  const [itemNameDraft, setItemNameDraft] = useState('');

  // Lazy-load on first expand to keep the page snappy.
  const ensureLoaded = async () => {
    if (loaded) return;
    try {
      const [is, cs] = await Promise.all([
        getFurnitureItemsCatalog(),
        getFurnitureItemCategories(),
      ]);
      setItems(is);
      setCategories(cs);
      setLoaded(true);
    } catch { toast.error('Could not load items catalog'); }
  };

  // Lightweight reload that doesn't blank the panel (preserves scroll).
  const reload = async () => {
    try {
      const [is, cs] = await Promise.all([
        getFurnitureItemsCatalog(),
        getFurnitureItemCategories(),
      ]);
      setItems(is);
      setCategories(cs);
    } catch { /* keep current view */ }
  };

  useEffect(() => { if (open) ensureLoaded(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const visible = q ? items.filter(i => i.name.toLowerCase().includes(q)) : items;
    const known = new Map();
    categories.forEach(c => known.set(c.name, []));
    const orphans = new Map();
    const uncat = [];
    const fromProjects = [];
    for (const it of visible) {
      const cat = it.category;
      if (!cat) {
        if (it.from_project) fromProjects.push(it);
        else uncat.push(it);
        continue;
      }
      if (known.has(cat)) known.get(cat).push(it);
      else {
        if (!orphans.has(cat)) orphans.set(cat, []);
        orphans.get(cat).push(it);
      }
    }
    const out = categories.map(c => ({
      key: c.id, id: c.id, name: c.name, items: known.get(c.name) || [],
    }));
    [...orphans.entries()].sort((a, b) => a[0].localeCompare(b[0])).forEach(([name, items]) => {
      out.push({ key: `orphan-${name}`, id: null, name, items });
    });
    out.push({ key: 'uncat', id: null, name: 'Uncategorized', items: uncat, isUncategorized: true });
    if (fromProjects.length) {
      out.push({ key: 'from-projects', id: null, name: 'From Past Projects', items: fromProjects, isFromProjects: true });
    }
    for (const g of out) g.items.sort((a, b) => a.name.localeCompare(b.name));
    return out.filter(g => g.items.length > 0 || (!q && g.isUncategorized));
  }, [items, categories, filter]);

  // ── Mutators ────────────────────────────────────────────────
  const addCategory = async () => {
    const n = newCatName.trim();
    if (!n) return;
    try { await createFurnitureItemCategory(n); setNewCatName(''); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not create category'); }
  };
  const saveCatName = async (cat) => {
    const next = catNameDraft.trim();
    if (!next || next === cat.name) { setRenamingCat(null); return; }
    try { await renameFurnitureItemCategory(cat.id, next); setRenamingCat(null); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Rename failed'); }
  };
  const removeCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? Items in it will move to Uncategorized.`)) return;
    try { await deleteFurnitureItemCategory(cat.id); await reload(); }
    catch { toast.error('Delete failed'); }
  };
  const addItem = async (categoryName) => {
    const n = newItemName.trim();
    if (!n) return;
    if (items.some(i => i.name.toLowerCase() === n.toLowerCase())) {
      toast.error('Item already exists in catalog');
      return;
    }
    try {
      await upsertFurnitureCatalogItem(n, { category: categoryName || null });
      setNewItemName('');
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add item'); }
  };
  const moveItem = async (entry, newCategory) => {
    try {
      await upsertFurnitureCatalogItem(entry.name, { category: newCategory || null });
      await reload();
    } catch { toast.error('Move failed'); }
  };
  const renameItem = async (entry) => {
    const next = itemNameDraft.trim();
    if (!next || next === entry.name) { setRenamingItemKey(null); return; }
    try {
      await renameFurnitureCatalogItem(entry.name, next);
      setRenamingItemKey(null);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rename failed'); }
  };
  const removeItem = async (entry) => {
    if (!window.confirm(`Remove "${entry.name}" from the catalog?`)) return;
    try { await deleteFurnitureCatalogItem(entry.name); await reload(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <Card className="border-dashed" data-testid="items-catalog-panel">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-[#f3efe6]/40"
      >
        {open ? <ChevronDown className="h-4 w-4 text-[#5b6773]" /> : <ChevronRight className="h-4 w-4 text-[#5b6773]" />}
        <Box className="h-4 w-4 text-[#2d948c]" />
        <span className="text-sm font-semibold text-[#1f2933]">Items Catalog</span>
        <span className="text-[11px] text-[#5b6773]">
          Standard items you can pick from when editing room defaults.
        </span>
        {readOnly && (
          <span className="ml-auto inline-flex items-center gap-1 text-amber-700 text-[11px] font-medium">
            <Lock className="h-3 w-3" /> Read-only
          </span>
        )}
      </button>

      {open && (
        <CardContent className="p-3 pt-0 border-t space-y-3">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center pt-3">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9ca3af]" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search items…"
                className="pl-8 h-8 text-xs"
                data-testid="items-catalog-search"
              />
            </div>
            {!readOnly && (
              <>
                <div className="flex items-center gap-1">
                  <Input
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
                    placeholder="New category"
                    className="h-8 text-xs w-32"
                    data-testid="items-catalog-new-category"
                  />
                  <Button size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-800" onClick={addCategory} disabled={!newCatName.trim()} data-testid="items-catalog-add-category">
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-1">
                  <Input
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(null); } }}
                    placeholder="New item"
                    className="h-8 text-xs w-36"
                    data-testid="items-catalog-new-item"
                  />
                  <Button size="sm" variant="outline" className="h-8" onClick={() => addItem(null)} disabled={!newItemName.trim()} data-testid="items-catalog-add-item">
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Groups */}
          {grouped.length === 0 ? (
            <p className="py-4 text-center text-xs text-[#9ca3af]">No items yet — add some via the inputs above.</p>
          ) : (
            <div className="space-y-2">
              {grouped.map(group => (
                <div key={group.key} className="border rounded">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#f3efe6]/30 border-b">
                    {renamingCat && renamingCat === group.id ? (
                      <Input
                        autoFocus
                        value={catNameDraft}
                        onChange={(e) => setCatNameDraft(e.target.value)}
                        onBlur={() => saveCatName(group)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                          if (e.key === 'Escape') setRenamingCat(null);
                        }}
                        className="h-6 text-xs w-40"
                      />
                    ) : (
                      <h4 className="text-xs font-semibold text-[#1f2933]">{group.name}</h4>
                    )}
                    <span className="text-[10px] text-[#9ca3af]">{group.items.length}</span>
                    {!readOnly && group.id && renamingCat !== group.id && (
                      <div className="ml-auto flex items-center gap-1">
                        <button
                          onClick={() => { setRenamingCat(group.id); setCatNameDraft(group.name); }}
                          className="text-[#5b6773]/70 hover:text-[#1f2933] p-0.5"
                          aria-label="Rename"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => removeCategory(group)}
                          className="text-[#a8324a]/70 hover:text-[#a8324a] p-0.5"
                          aria-label="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="px-2 py-1.5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-2 gap-y-0.5">
                    {group.items.length === 0 ? (
                      <p className="text-[10px] text-[#9ca3af] italic px-1 py-0.5 col-span-full">Empty.</p>
                    ) : group.items.map(entry => (
                      <div
                        key={entry.name_key}
                        className="group flex items-center gap-1 px-1 py-0.5 rounded hover:bg-[#2d948c]/5"
                        data-testid={`items-catalog-row-${entry.name_key.replace(/\s+/g, '-')}`}
                      >
                        {renamingItemKey === entry.name_key ? (
                          <Input
                            autoFocus
                            value={itemNameDraft}
                            onChange={(e) => setItemNameDraft(e.target.value)}
                            onBlur={() => renameItem(entry)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                              if (e.key === 'Escape') setRenamingItemKey(null);
                            }}
                            className="h-6 text-[11px] flex-1"
                          />
                        ) : (
                          <span
                            className={`text-[11px] text-[#1f2933] truncate flex-1 ${readOnly || entry.from_project ? '' : 'cursor-text'}`}
                            title={entry.from_project ? 'Used in a past project — assign a category to add it to the catalog.' : undefined}
                            onClick={() => { if (!readOnly && !entry.from_project) { setRenamingItemKey(entry.name_key); setItemNameDraft(entry.name); } }}
                          >
                            {entry.name}
                          </span>
                        )}
                        {!readOnly && renamingItemKey !== entry.name_key && (
                          <>
                            <select
                              onChange={(e) => moveItem(entry, e.target.value || null)}
                              value={entry.category || ''}
                              className="text-[9px] text-[#5b6773] bg-transparent border-0 opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none cursor-pointer max-w-[60px]"
                              aria-label="Move"
                              title={entry.from_project ? 'Promote to a catalog category' : 'Move to category'}
                            >
                              <option value="">— uncat —</option>
                              {categories.map(c => (
                                <option key={c.id} value={c.name}>{c.name}</option>
                              ))}
                            </select>
                            {!entry.from_project && (
                              <button
                                onClick={() => removeItem(entry)}
                                className="text-[#a8324a]/70 hover:text-[#a8324a] opacity-0 group-hover:opacity-100"
                                aria-label="Remove"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
